import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, lastValueFrom, from } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

const GRAPH_SCOPES = ['https://graph.microsoft.com/.default'];

@Injectable({
    providedIn: 'root'
})
export class GraphService {
    constructor(
        private http: HttpClient,
        private msalService: MsalService
    ) { }

    private async getGraphToken(): Promise<string> {
        const account = this.msalService.instance.getActiveAccount();

        console.log('[GraphService] getGraphToken - Acquiring Graph token for active account');

        if (!account) {
            throw new Error('[GraphService] No active MSAL account. User must be logged in.');
        }

        const tokenResponse = await lastValueFrom(
            this.msalService.acquireTokenSilent({
                scopes: GRAPH_SCOPES,
                account: account
            })
        );

        console.log('[GraphService] getGraphToken - Graph token acquired successfully');
        return tokenResponse.accessToken;
    }

    public async getUserPhoto(): Promise<any> {
        const p = new Promise<any>((resolve, reject) => {
            this.getGraphToken().then(graphToken => {
                let url = "https://graph.microsoft.com/v1.0/me/photo/$value";
                let headers: HttpHeaders = new HttpHeaders({
                    Authorization: `Bearer ${graphToken}`
                });

                console.log('[GraphService] getUserPhoto - Fetching user photo from Graph API');

                let stream$ = this.http.get(url, { headers: headers, responseType: "blob" });

                if (stream$) {
                    lastValueFrom(stream$).then((response: any) => {
                        if (response) {
                            let reader: FileReader = new FileReader();

                            reader.readAsDataURL(response);
                            reader.onloadend = () => {
                                let photo = reader.result;
                                console.log('[GraphService] getUserPhoto - Photo fetched successfully');
                                resolve(photo);
                            };
                        }
                    }).catch(err => {
                        console.error('[GraphService] getUserPhoto - Error fetching photo:', err);
                        reject(err);
                    });
                }
            }).catch(err => {
                console.error('[GraphService] getUserPhoto - Error acquiring graph token:', err);
                reject(err);
            });
        });

        return await p;
    }

    public getMemberOf(): Observable<any[]> {
        return from(this.getGraphToken()).pipe(
            switchMap(graphToken => {
                const headers = new HttpHeaders({ Authorization: `Bearer ${graphToken}` });
                return this.http.get<any>('https://graph.microsoft.com/v1.0/me/memberOf', { headers });
            }),
            map((response: any) => response?.value || [])
        );
    }

    public async getUserGroups(role: any): Promise<any> {
        let results: any = null;

        try {
            console.log('[GraphService] getUserGroups - Fetching groups from Graph API');

            const graphToken = await this.getGraphToken();

            let url = `https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$count=true&$orderby=displayName&$filter=startswith(displayName, '${role}')`;
            let headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${graphToken}`,
                ConsistencyLevel: "eventual"
            });

            let stream$ = this.http.get(url, { headers: headers });
            results = await lastValueFrom(stream$);

            console.log('[GraphService] getUserGroups - Groups fetched, count:', (results as any)?.value?.length);
        } catch (error: any) {
            console.error('[GraphService] getUserGroups - Error:', error.message);
            throw error;
        }

        return results;
    }

    /**
     * Checks if the current user is a member of a specific AD group by object ID
     * @param requiredGroupObjectId The object ID of the AD group to check membership for
     * @returns Promise with { isMember: boolean }
     */
    public async isUserInRequiredGroup(requiredGroupObjectId: string): Promise<boolean> {
        try {
            console.log('[GraphService] isUserInRequiredGroup - Checking membership for group:', requiredGroupObjectId);

            const groups = await lastValueFrom(this.getMemberOf());
            const isMember = groups.some((group: any) => group.id === requiredGroupObjectId);

            if (isMember) {
                console.log('[GraphService] isUserInRequiredGroup - User is member of required group:', requiredGroupObjectId);
            } else {
                console.log('[GraphService] isUserInRequiredGroup - User is NOT member of required group:', requiredGroupObjectId);
            }

            return isMember;
        } catch (error: any) {
            console.error('[GraphService] isUserInRequiredGroup - Error checking group membership:', error.message);
            throw error;
        }
    }
}

